<template>
  <div
    class="chat-anchor z-[900] flex flex-col-reverse items-end gap-3"
    :class="embedded ? 'chat-anchor--embedded' : 'fixed'"
  >
    <ChatFab
      v-if="!embedded"
      :open="open"
      :new-msg-count="resolvedNewMsgCount"
      @toggle="openOrCloseChat"
    />
    <ChatWindow
      v-if="isWindowOpen"
      class="chat-window"
      :class="embedded ? 'chat-window--embedded' : ''"
      :style="embedded ? undefined : chatWindowStyle"
      v-model="messageText"
      :messages="displayedMessages"
      :loading="loadingHistory"
      :error-text="errorText"
      :manager-name="managerName"
      :manager-online="managerOnline"
      :sending="sending"
      :uploading-file="uploadingFile"
      :can-upload-file="canUploadFile"
      :file-warning-text="fileWarningText"
      :search-open="searchOpen"
      :search-text="searchText"
      :search-date-from="searchDateFrom"
      :search-date-to="searchDateTo"
      :search-loading="searchLoading"
      :search-error="searchError"
      :highlight-query="highlightQuery"
      :highlight-message-ids="highlightMessageIds"
      @open-search="openSearch"
      @close-search="closeSearch"
      @update:search-text="searchText = $event"
      @update:search-date-from="searchDateFrom = $event"
      @update:search-date-to="searchDateTo = $event"
      @search="runSearch"
      @send="handleSend"
      @attach-files="handleAttachFiles"
    />
  </div>
</template>

<script lang="ts" setup>
import ChatFab from "~/components/chat/ChatFab.vue";
import ChatWindow from "~/components/chat/ChatWindow.vue";
import { useHelpChatApi } from "~/composables/useHelpChatApi";
import type {
  HelpChatFile,
  HelpChatHistoryByTicket,
  HelpChatIncomingByTicket,
  HelpChatManager,
  HelpChatManagerStatus,
  HelpChatUiMessage,
} from "~/types/helpChat";

const props = withDefaults(
  defineProps<{
    newMsgCount?: number;
    embedded?: boolean;
  }>(),
  {
    newMsgCount: 0,
    embedded: false,
  },
);

const open = ref(false);
const messageText = ref("");
const loadingHistory = ref(false);
const sending = ref(false);
const uploadingFile = ref(false);
const errorText = ref("");
const fileWarningText = ref("");
const messages = ref<HelpChatUiMessage[]>([]);
const unreadCount = ref(0);
const managerName = ref("");
const managerOnline = ref(false);
const searchOpen = ref(false);
const searchText = ref("");
const searchDateFrom = ref("");
const searchDateTo = ref("");
const searchLoading = ref(false);
const searchError = ref("");
const highlightQuery = ref("");
const highlightMessageIds = ref<number[]>([]);
const polling = ref(false);
const reading = ref(false);
const HISTORY_PAGE_LIMIT = 200;
const MESSAGE_MAX_LENGTH = 500;
const MAX_CHAT_FILE_SIZE_BYTES = 15 * 1024 * 1024;
const FILE_WARNING_VISIBILITY_MS = 10_000;
const MANAGER_STATUS_REFRESH_INTERVAL_MS = 30_000;
const LAYOUT_HEADER_HEIGHT_PX = 95;
const CHAT_MARGIN_PX = 12;
const CHAT_FAB_SIZE_PX = 70;
const CHAT_STACK_GAP_PX = 12;
const CHAT_HEIGHT_RESERVE_PX =
  CHAT_MARGIN_PX + CHAT_FAB_SIZE_PX + CHAT_STACK_GAP_PX;
const CHAT_MAX_HEIGHT_PX = 780;
let nextIncomingOrderHint = 1;
let nextLocalOrderHint = Number.MAX_SAFE_INTEGER - 10_000;
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let fileWarningTimer: ReturnType<typeof setTimeout> | null = null;
let managerStatusTimer: ReturnType<typeof setTimeout> | null = null;
let managerStatusPromise: Promise<void> | null = null;
let lastSearchRequestId = 0;
const pendingReadUuids = new Set<string>();

const {
  getClientHistory,
  getNewMessages,
  sendClientText,
  sendClientFile,
  markAsRead,
  getClientManagerStatus,
  searchClientMessages,
} = useHelpChatApi();

const highlightedMessageIdSet = computed(
  () => new Set(highlightMessageIds.value),
);
const embedded = computed(() => Boolean(props.embedded));
const isWindowOpen = computed(() => embedded.value || open.value);
const chatWindowStyle = computed(() => {
  const widthLimit = `max(0px, calc(100vw - ${CHAT_MARGIN_PX * 2}px - env(safe-area-inset-left) - env(safe-area-inset-right)))`;
  const heightLimit = `max(0px, calc(100dvh - ${LAYOUT_HEADER_HEIGHT_PX + CHAT_HEIGHT_RESERVE_PX}px - env(safe-area-inset-top) - env(safe-area-inset-bottom)))`;

  return {
    width: `min(505px, ${widthLimit})`,
    maxHeight: heightLimit,
    height: `min(${CHAT_MAX_HEIGHT_PX}px, ${heightLimit})`,
  };
});
const displayedMessages = computed<HelpChatUiMessage[]>(() => {
  if (!searchOpen.value) {
    return messages.value;
  }

  const query = searchText.value.trim();
  if (!query || !highlightMessageIds.value.length) {
    return [];
  }

  return messages.value.filter(
    (message) =>
      typeof message.serverId === "number" &&
      highlightedMessageIdSet.value.has(message.serverId),
  );
});
const activeTicketId = computed(() => {
  for (let index = messages.value.length - 1; index >= 0; index -= 1) {
    const ticketId = Number(messages.value[index]?.ticketId);
    if (Number.isFinite(ticketId) && ticketId > 0) {
      return ticketId;
    }
  }

  return null;
});
const canUploadFile = computed(() => activeTicketId.value !== null);

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) {
    const message = error.message.trim();
    if (message) {
      return message;
    }
  }

  return fallback;
};

const clearFileWarning = () => {
  fileWarningText.value = "";
  if (!fileWarningTimer) return;
  clearTimeout(fileWarningTimer);
  fileWarningTimer = null;
};

const showFileWarning = (message: string) => {
  fileWarningText.value = message;
  if (fileWarningTimer) {
    clearTimeout(fileWarningTimer);
  }
  fileWarningTimer = setTimeout(() => {
    fileWarningText.value = "";
    fileWarningTimer = null;
  }, FILE_WARNING_VISIBILITY_MS);
};

const resetManagerState = () => {
  managerName.value = "";
  managerOnline.value = false;
};

const applyManagerStatus = (status?: HelpChatManagerStatus | null) => {
  if (!status?.assigned) {
    resetManagerState();
    return;
  }

  const manager = normalizeManagerPayload(status.manager);
  if (!manager?.name) {
    resetManagerState();
    return;
  }

  managerName.value = manager.name;
  managerOnline.value = Boolean(manager.online);
};

const stopManagerStatusRefresh = () => {
  if (!managerStatusTimer) return;
  clearTimeout(managerStatusTimer);
  managerStatusTimer = null;
};

const scheduleManagerStatusRefresh = (
  delayMs = MANAGER_STATUS_REFRESH_INTERVAL_MS,
) => {
  stopManagerStatusRefresh();
  if (!open.value) return;

  managerStatusTimer = setTimeout(() => {
    managerStatusTimer = null;
    void refreshManagerStatus();
  }, delayMs);
};

const refreshManagerStatus = async () => {
  if (managerStatusPromise) return managerStatusPromise;

  stopManagerStatusRefresh();
  managerStatusPromise = (async () => {
    try {
      const response = await getClientManagerStatus();
      applyManagerStatus(response);
    } catch (error) {
      console.error("Не удалось получить статус менеджера", error);
    } finally {
      managerStatusPromise = null;
      scheduleManagerStatusRefresh();
    }
  })();

  return managerStatusPromise;
};

const appendLocalMessage = (payload: {
  ticketId: number;
  text?: string;
  file?: HelpChatFile;
}) => {
  const ticketId = Number(payload.ticketId);
  if (!Number.isFinite(ticketId) || ticketId <= 0) return;

  const orderHint = nextLocalOrderHint++;
  const localMessage: HelpChatUiMessage = {
    key: `local:${ticketId}:${orderHint}`,
    ticketId,
    text: String(payload.text ?? ""),
    createAt: new Date().toISOString(),
    fromClient: true,
    fromManager: false,
    fromSystem: false,
    file: payload.file,
    orderHint,
  };

  messages.value = [...messages.value, localMessage].sort(sortMessages);
};

const normalizeFileAttachment = (value: unknown): HelpChatFile | undefined => {
  if (!value || typeof value !== "object") return undefined;

  const file = value as Record<string, unknown>;
  const id = Number(file.id);
  if (!Number.isFinite(id) || id <= 0) return undefined;

  const path = String(file.path ?? "").trim();
  const fallbackName = path.split("/").pop() || "";
  const name = String(file.name ?? fallbackName).trim();

  return {
    id,
    path,
    name: name || fallbackName || `file-${id}`,
  };
};

const normalizeManagerPayload = (
  value: unknown,
): HelpChatManager | undefined => {
  if (!value || typeof value !== "object") return undefined;

  const manager = value as Record<string, unknown>;
  const userId = Number(manager.user_id);
  const name = String(manager.name ?? "").trim();
  if (!Number.isFinite(userId) || !name) return undefined;

  return {
    user_id: userId,
    name,
    online: Boolean(manager.online),
  };
};

const parseTimestamp = (value: string) => {
  const direct = Date.parse(value);
  if (Number.isFinite(direct)) return direct;

  const legacy = value.match(
    /^(\d{2})\.(\d{2})\.(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (!legacy) return Number.MAX_SAFE_INTEGER;

  const [, day, month, year, hour, minute, second = "0"] = legacy;
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  ).getTime();

  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

const sortMessages = (left: HelpChatUiMessage, right: HelpChatUiMessage) => {
  const byDate = parseTimestamp(left.createAt) - parseTimestamp(right.createAt);
  if (byDate !== 0) return byDate;

  const leftServerId =
    typeof left.serverId === "number" ? left.serverId : Number.MAX_SAFE_INTEGER;
  const rightServerId =
    typeof right.serverId === "number"
      ? right.serverId
      : Number.MAX_SAFE_INTEGER;
  if (leftServerId !== rightServerId) return leftServerId - rightServerId;

  if (
    typeof left.orderHint === "number" &&
    typeof right.orderHint === "number" &&
    left.orderHint !== right.orderHint
  ) {
    return left.orderHint - right.orderHint;
  }

  if (left.ticketId !== right.ticketId) return left.ticketId - right.ticketId;
  return left.key.localeCompare(right.key);
};

const normalizeHistoryMessages = (
  items?: HelpChatHistoryByTicket | null,
): HelpChatUiMessage[] => {
  if (!items || typeof items !== "object") return [];

  const normalized: HelpChatUiMessage[] = [];

  Object.entries(items).forEach(([ticketKey, ticketMessages]) => {
    const fallbackTicketId = Number(ticketKey);
    const list = Array.isArray(ticketMessages) ? ticketMessages : [];

    list.forEach((message) => {
      const ticketId = Number(message.ticket_id);
      const serverIdRaw = Number(message.id);
      const serverId = Number.isFinite(serverIdRaw) ? serverIdRaw : undefined;

      normalized.push({
        key: `history:${message.id}`,
        ticketId: Number.isFinite(ticketId)
          ? ticketId
          : Number.isFinite(fallbackTicketId)
            ? fallbackTicketId
            : 0,
        text: String(message.text ?? ""),
        createAt: String(message.create_at ?? ""),
        fromClient: Boolean(message.from_client),
        fromManager: Boolean(message.from_manager),
        fromSystem: Boolean(message.from_system),
        file: normalizeFileAttachment(message.file),
        manager: normalizeManagerPayload(message.manager),
        serverId,
        orderHint: serverId,
      });
    });
  });

  return normalized.sort(sortMessages);
};

const normalizeIncomingMessages = (
  items?: HelpChatIncomingByTicket | null,
): HelpChatUiMessage[] => {
  if (!items || typeof items !== "object") return [];

  const normalized: HelpChatUiMessage[] = [];

  Object.entries(items).forEach(([ticketKey, ticketMessages]) => {
    const fallbackTicketId = Number(ticketKey);
    const list = Array.isArray(ticketMessages) ? ticketMessages : [];

    list.forEach((message, index) => {
      const ticketId = Number(message.ticket_id);
      const uuid = typeof message.uuid === "string" ? message.uuid : "";
      const manager = normalizeManagerPayload(message.manager);
      const fromManager = Boolean(manager);
      normalized.push({
        key: uuid
          ? `incoming:${uuid}`
          : `incoming:${ticketId}:${message.create_at}:${index}`,
        ticketId: Number.isFinite(ticketId)
          ? ticketId
          : Number.isFinite(fallbackTicketId)
            ? fallbackTicketId
            : 0,
        text: String(message.text ?? ""),
        createAt: String(message.create_at ?? ""),
        fromClient: false,
        fromManager,
        fromSystem: !fromManager,
        file: normalizeFileAttachment(message.file),
        manager,
        uuid: uuid || undefined,
        orderHint: nextIncomingOrderHint++,
      });
    });
  });

  return normalized.sort(sortMessages);
};

const mergeIncoming = (incoming: HelpChatUiMessage[]) => {
  if (!incoming.length) return;
  const map = new Map(messages.value.map((message) => [message.key, message]));
  const inserted: HelpChatUiMessage[] = [];
  incoming.forEach((message) => {
    if (!map.has(message.key)) {
      map.set(message.key, message);
      inserted.push(message);
    }
  });
  messages.value = Array.from(map.values()).sort(sortMessages);
  return inserted;
};

const resolvedNewMsgCount = computed(() => {
  const explicit = Number(props.newMsgCount);
  if (Number.isFinite(explicit) && explicit > 0) {
    return Math.floor(explicit);
  }
  return unreadCount.value;
});

const clearSearchHighlight = () => {
  highlightQuery.value = "";
  highlightMessageIds.value = [];
  searchError.value = "";
};

const openSearch = () => {
  searchOpen.value = true;
};

const closeSearch = () => {
  searchOpen.value = false;
  searchText.value = "";
  searchDateFrom.value = "";
  searchDateTo.value = "";
  searchLoading.value = false;
  clearSearchHighlight();
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = null;
  }
};

const extractMatchedIds = (items?: HelpChatHistoryByTicket | null) => {
  if (!items || typeof items !== "object") return [];

  const ids = new Set<number>();
  Object.values(items).forEach((messagesByTicket) => {
    const list = Array.isArray(messagesByTicket) ? messagesByTicket : [];
    list.forEach((message) => {
      const id = Number(message.id);
      if (Number.isFinite(id)) {
        ids.add(id);
      }
    });
  });

  return Array.from(ids).sort((a, b) => a - b);
};

const runSearch = async () => {
  if (!searchOpen.value) return;

  const query = searchText.value.trim();
  if (!query) {
    clearSearchHighlight();
    return;
  }

  const requestId = ++lastSearchRequestId;
  searchLoading.value = true;
  highlightQuery.value = query;
  highlightMessageIds.value = [];
  searchError.value = "";
  try {
    const response = await searchClientMessages(
      query,
      searchDateFrom.value || undefined,
      searchDateTo.value || undefined,
    );

    if (requestId !== lastSearchRequestId) return;

    highlightMessageIds.value = extractMatchedIds(response);
  } catch (error) {
    if (requestId !== lastSearchRequestId) return;
    console.error("Не удалось выполнить поиск по чату", error);
    searchError.value = "Не удалось выполнить поиск";
    highlightMessageIds.value = [];
  } finally {
    if (requestId === lastSearchRequestId) {
      searchLoading.value = false;
    }
  }
};

const scheduleSearch = () => {
  if (!searchOpen.value) return;
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
  searchDebounceTimer = setTimeout(() => {
    void runSearch();
  }, 300);
};

const openOrCloseChat = () => {
  if (embedded.value) return;
  open.value = !open.value;
};

const flushPendingReadUuids = async () => {
  if (!open.value || !pendingReadUuids.size || reading.value) return;

  reading.value = true;
  try {
    const uuids = Array.from(pendingReadUuids);
    await markAsRead(uuids);
    uuids.forEach((uuid) => pendingReadUuids.delete(uuid));
  } catch (error) {
    console.error("Не удалось пометить сообщения прочитанными", error);
  } finally {
    reading.value = false;
  }
};

const loadHistory = async (silent = false) => {
  if (!silent) {
    loadingHistory.value = true;
  }
  try {
    const allMessages: HelpChatUiMessage[] = [];
    let page = 1;

    while (true) {
      const response = await getClientHistory(page, HISTORY_PAGE_LIMIT);
      const normalized = normalizeHistoryMessages(response?.items);
      if (!normalized.length) break;

      allMessages.push(...normalized);
      if (normalized.length < HISTORY_PAGE_LIMIT) break;

      page += 1;
    }

    const uniqueMap = new Map(
      allMessages.map((message) => [message.key, message]),
    );
    messages.value = Array.from(uniqueMap.values()).sort(sortMessages);
    errorText.value = "";
  } catch (error) {
    console.error("Не удалось загрузить историю чата", error);
    if (!messages.value.length) {
      errorText.value = "Не удалось загрузить историю чата";
    }
  } finally {
    if (!silent) {
      loadingHistory.value = false;
    }
  }
};

const pollNewMessages = async () => {
  if (polling.value) return;
  polling.value = true;

  while (polling.value) {
    try {
      const response = await getNewMessages();
      const incoming = normalizeIncomingMessages(response);

      if (!incoming.length) {
        continue;
      }

      const inserted = mergeIncoming(incoming) || [];
      if (!inserted.length) {
        continue;
      }

      if (!open.value) {
        unreadCount.value += inserted.length;
      }

      const uuids = inserted
        .map((message) => message.uuid)
        .filter((uuid): uuid is string => Boolean(uuid));

      if (uuids.length) {
        uuids.forEach((uuid) => pendingReadUuids.add(uuid));
      }

      if (open.value) {
        await nextTick();
        await flushPendingReadUuids();
      }

      void refreshManagerStatus();
      errorText.value = "";
    } catch (error) {
      console.error("Не удалось получить новые сообщения", error);
      if (open.value && !messages.value.length) {
        errorText.value = "Не удалось получить новые сообщения";
      }
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
  }
};

const startPolling = () => {
  if (polling.value) return;
  void pollNewMessages();
};

const stopPolling = () => {
  polling.value = false;
};

watch([searchText, searchDateFrom, searchDateTo], () => {
  if (!searchOpen.value) return;
  highlightQuery.value = searchText.value.trim();
  highlightMessageIds.value = [];
  searchError.value = "";
  scheduleSearch();
});

watch(open, async (isOpen) => {
  if (isOpen) {
    unreadCount.value = 0;
    await nextTick();
    await flushPendingReadUuids();
    await loadHistory();
    await refreshManagerStatus();
    return;
  }

  stopManagerStatusRefresh();
  managerStatusPromise = null;
});

onMounted(() => {
  if (embedded.value) {
    open.value = true;
  }
  startPolling();
});

onBeforeUnmount(() => {
  stopPolling();
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = null;
  }
  if (fileWarningTimer) {
    clearTimeout(fileWarningTimer);
    fileWarningTimer = null;
  }
  stopManagerStatusRefresh();
  managerStatusPromise = null;
});

const handleSend = async () => {
  const text = messageText.value.trim().slice(0, MESSAGE_MAX_LENGTH);
  if (!text || sending.value) return;

  sending.value = true;
  try {
    const ticketId = await sendClientText(text);
    appendLocalMessage({ ticketId, text });
    messageText.value = "";
    errorText.value = "";
    void refreshManagerStatus();
  } catch (error) {
    console.error("Не удалось отправить сообщение", error);
    errorText.value = extractErrorMessage(
      error,
      "Не удалось отправить сообщение",
    );
  } finally {
    sending.value = false;
  }
};

const handleAttachFiles = async (files: File[]) => {
  if (uploadingFile.value) return;

  const normalizedFiles = files.filter(
    (file) => file instanceof File && file.size >= 0,
  );
  if (!normalizedFiles.length) return;

  const ticketId = activeTicketId.value;
  if (!ticketId) {
    errorText.value = "Сначала отправьте сообщение, затем прикрепите файл";
    return;
  }

  const oversizedFiles = normalizedFiles.filter(
    (file) => file.size > MAX_CHAT_FILE_SIZE_BYTES,
  );
  const uploadableFiles = normalizedFiles.filter(
    (file) => file.size <= MAX_CHAT_FILE_SIZE_BYTES,
  );

  if (oversizedFiles.length) {
    showFileWarning(
      oversizedFiles.length === 1
        ? "Один из файлов больше 15 МБ и не будет загружен"
        : "Некоторые файлы больше 15 МБ и не будут загружены",
    );
  } else {
    clearFileWarning();
  }

  if (!uploadableFiles.length) {
    return;
  }

  uploadingFile.value = true;
  let currentTicketId = ticketId;
  let uploadedCount = 0;
  let failedCount = 0;
  let firstFailedMessage = "";

  try {
    for (const file of uploadableFiles) {
      try {
        const response = await sendClientFile(currentTicketId, file);
        const fileId = Number(response?.file_id);
        if (!Number.isFinite(fileId) || fileId <= 0) {
          throw new Error(
            "Не удалось получить идентификатор загруженного файла",
          );
        }
        const filePath = String(response?.path ?? "").trim();
        currentTicketId = Number(response?.ticket_id) || currentTicketId;

        appendLocalMessage({
          ticketId: currentTicketId,
          file: {
            id: fileId,
            path: filePath,
            name: file.name || filePath.split("/").pop() || `file-${fileId}`,
          },
        });
        uploadedCount += 1;
      } catch (error) {
        failedCount += 1;
        firstFailedMessage ||= extractErrorMessage(
          error,
          `Не удалось загрузить файл ${file.name}`,
        );
        console.error(`Не удалось загрузить файл "${file.name}"`, error);
      }
    }
  } catch (error) {
    console.error("Не удалось загрузить файлы", error);
    errorText.value = extractErrorMessage(error, "Не удалось загрузить файлы");
  } finally {
    uploadingFile.value = false;
  }

  if (failedCount) {
    showFileWarning(
      uploadedCount > 0
        ? failedCount === 1
          ? "Один файл не удалось загрузить"
          : `Не удалось загрузить ${failedCount} файлов`
        : firstFailedMessage || "Не удалось загрузить файлы",
    );
  }

  if (uploadedCount > 0) {
    errorText.value = "";
    void refreshManagerStatus();
    return;
  }

  if (!failedCount) {
    errorText.value = "";
    return;
  }

  errorText.value = firstFailedMessage || "Не удалось загрузить файлы";
};
</script>

<style scoped>
.chat-anchor {
  right: max(12px, env(safe-area-inset-right));
  bottom: max(12px, env(safe-area-inset-bottom));
}

.chat-anchor--embedded {
  right: auto;
  bottom: auto;
  width: 100%;
  height: 100%;
  gap: 0;
}

.chat-window--embedded {
  height: 100%;
  max-height: none;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

@media (min-width: 1176px) {
  .chat-window--embedded {
    border: 1px solid var(--color-gray-border);
    border-radius: 24px;
  }
}
</style>
